/**
 * Viewport clip + membership finalize (EPIC-VIEWPORT-CLIP SLICE 3 —
 * `S-VIEWPORT-CLIP-RESOLVE`, shared post-build pass).
 *
 * The two typed projections that complete a paper-space VIEWPORT after the
 * entity-routing bridge has assembled the `DrawingDocument`:
 *
 *   (1) `clipPath` — `resolveViewportClip` joins each VIEWPORT (via its DXF
 *       extension-dictionary → ACAD_FILTER → SPATIAL → SPATIAL_FILTER chain) to
 *       its clip boundary, transformed to WCS by the inverse-insert matrix.
 *   (2) `childEntityIds` — `buildViewportMembershipIndex` computes, per VIEWPORT,
 *       the model-space entity ids whose geometry falls inside the viewport's
 *       model-space view rectangle.
 *
 * WHY a SHARED function (extracted from the inline blocks that used to live at
 * the tail of `importDxfDocumentToDrawingDocument`): the slice-3 passes must run
 * on BOTH import paths to AutoCAD parity (ПРИОРИТЕТ #1 — full-fidelity import).
 * The synchronous bridge has all inputs in hand at one site. The async/worker
 * STREAMING path (huge-file `parseFile`) discards the model-space entities and
 * DRAINS the raw top-level DXF entities for memory, so by the time the inline
 * pass ran its inputs were already gone (clipPath/childEntityIds resolved to
 * null). Splitting the pass out lets each path supply EXACTLY the inputs it has:
 *
 *   - The clip pass needs the raw OBJECTS section (`spatialFilters` +
 *     `dictionaries`) + each VIEWPORT's `extensionDictionaryHandle`. The raw
 *     OBJECTS section is only alive inside the bridge/worker realm; on the
 *     streaming path the clip pass therefore runs INSIDE the worker (objects
 *     intact, Viewports retained), feeding the RETAINED typed viewports as a
 *     light raw shim (the raw top-level entities array is already drained).
 *   - The membership pass needs every model-space entity. On the streaming path
 *     those live ONLY in the main-thread compact cache, so it runs on the MAIN
 *     thread after reconstruction, fed `modelSpaceEntities` from the cache.
 *
 * Both halves are independent + idempotent: callers pass `skipClip` /
 * `skipMembership` (and the relevant inputs) to run only the half they can.
 */

import type {
  DxfObjectsSection,
  DxfSupportedEntity,
} from "@dxf-ai-editor/dxf-io";
import { normalizeHandle, resolveViewportClip } from "@dxf-ai-editor/dxf-io";

import { bulgeArc, bulgeArcPointAt } from "./bulgeArc";
import { viewportWcsViewCenter } from "./viewportViewFrame";
import type {
  BlockChildEntity,
  DrawingDocument,
  DrawingEntity,
  LwpolylineEntity,
  ViewportEntity,
} from "./model";
import {
  buildViewportMembershipIndex,
  computeBlockDefinitionBounds,
} from "./viewportMembership";

/**
 * Block-definition shape consumed by `computeBlockDefinitionBounds`. `basePoint`
 * (DXF BLOCK group 10/20) is subtracted before the insert transform so a block
 * authored around a non-zero base point computes its REAL footprint, matching
 * the renderer (Codex #326 P2).
 */
type BlockDefForBounds = {
  entities: ReadonlyArray<BlockChildEntity>;
  basePoint?: { x: number; y: number };
};

export type ApplyViewportClipAndMembershipOptions = {
  /**
   * Raw DXF VIEWPORT entities (with `common.extensionDictionaryHandle`) used by
   * the clip resolver. Supply these when the raw entity array is still intact
   * (the synchronous bridge / non-streaming path). When omitted, a lightweight
   * raw shim is built from the document's TYPED viewports — the resolver only
   * reads `type`, `common.handle`, `common.extensionDictionaryHandle`, all of
   * which round-trip on the typed `ViewportEntity` — so the clip pass still
   * resolves even after the raw top-level entities were drained.
   */
  clipViewports?: ReadonlyArray<DxfSupportedEntity>;
  /**
   * Skip the SPATIAL_FILTER (XCLIP-style) clip half (e.g. the main-thread
   * streaming finalize, where the raw OBJECTS section is unavailable because it
   * was consumed in the worker — the worker already set `clipPath` for that
   * half). Default false. Does NOT skip the native group-340 boundary half (see
   * `skipNativeBoundaryClip`) — that half needs paper-space entities, not the
   * OBJECTS section, so the two clip halves are independently skippable.
   */
  skipClip?: boolean;
  /**
   * Skip the native group-340 boundary-entity clip half. The worker streaming
   * path SKIPS this half (the referenced paper-space boundary entity is streamed
   * out and not reliably retained in the bridge shell, so it may be unresolvable
   * inside the worker); the MAIN thread re-runs ONLY this half afterwards with
   * `paperSpaceEntities` read back from the compact cache (Codex #326 P2). The
   * synchronous (non-streaming) bridge runs both halves in one shot. Default
   * false.
   */
  skipNativeBoundaryClip?: boolean;
  /**
   * Paper-space entities the native group-340 boundary lookup joins against.
   * Supply when the boundary entities do NOT live in the document's paper spaces
   * (the streaming main-thread finalize keeps them only in the compact cache).
   * When omitted, the document's own paper-space `entities[]` are scanned.
   */
  paperSpaceEntities?: ReadonlyArray<DrawingEntity>;
  /**
   * Model-space entities for the membership pass, as any single-pass `Iterable`
   * (an array OR a lazy generator). Supply when they do NOT live in the
   * document's model space (the streaming path keeps them only in the main-thread
   * compact cache). When omitted, the document's model-space `entities[]` is used.
   *
   * The membership pass walks this EXACTLY ONCE and retains only a slim
   * `{id, AABB}` per entity, so a streaming caller can pass a compact-cache
   * generator (`iterateSpaceEntity`) and avoid materialising the full
   * `DrawingEntity[]` snapshot — which on a large model space is hundreds of MB
   * and defeats the streaming memory cap (Codex #326 r3 P2 #4).
   */
  modelSpaceEntities?: Iterable<DrawingEntity>;
  /**
   * Skip the membership half (e.g. the worker streaming pass, where the
   * model-space entities were discarded — membership runs later on the main
   * thread). Default false.
   */
  skipMembership?: boolean;
};

export type ApplyViewportClipAndMembershipResult = {
  /** Count of VIEWPORTs whose ext-dict→ACAD_FILTER→SPATIAL chain dangled. */
  orphanViewportClipReferences: number;
};

/**
 * Collect every paper-space (non-model) VIEWPORT entity in the document, in
 * space + insertion order. (Model space does not carry VIEWPORT entities; the
 * guard is inclusive so any unusual DXF structure still resolves.)
 */
function collectPaperViewports(document: DrawingDocument): ViewportEntity[] {
  const viewports: ViewportEntity[] = [];
  for (const space of document.spaces) {
    for (const entity of space.entities) {
      if (entity.type === "Viewport") viewports.push(entity);
    }
  }
  return viewports;
}

/**
 * Build a minimal raw-DXF VIEWPORT shim from the typed viewports so the clip
 * resolver (which takes `DxfSupportedEntity[]`) can run even when the raw
 * top-level entities array was drained. The resolver reads only `type` +
 * `common.handle` + `common.extensionDictionaryHandle`.
 *
 * The typed `ViewportEntity` carries the entity extension-dictionary 360 handle
 * as a TOP-LEVEL `extensionDictionaryHandle` field (it lives on
 * `drawingEntityBaseSchema`, spread flat onto every entity — NOT under a nested
 * `common`), and its `id` IS the DXF handle. Map those onto the raw common shape
 * the resolver expects.
 */
function rawViewportShimsFromTyped(
  viewports: ReadonlyArray<ViewportEntity>,
): DxfSupportedEntity[] {
  return viewports
    .filter((vp) => vp.extensionDictionaryHandle)
    .map(
      (vp) =>
        ({
          type: "VIEWPORT",
          common: {
            handle: vp.id,
            extensionDictionaryHandle: vp.extensionDictionaryHandle,
          },
        }) as unknown as DxfSupportedEntity,
    );
}

/**
 * Read a raw-DXF VIEWPORT shim/entity's handle (group 5), normalized for
 * case-insensitive comparison. The raw shape carries it under `common.handle`.
 */
function rawViewportHandle(vp: DxfSupportedEntity): string | undefined {
  const handle = (vp as { common?: { handle?: string } }).common?.handle;
  return handle ? normalizeHandle(handle) : undefined;
}

/**
 * Union the caller-supplied raw VIEWPORT list (the pre-drain source of truth,
 * possibly EMPTY or carrying only block-definition VIEWPORTs on the drained
 * streaming path) with a typed shim for every retained paper viewport NOT already
 * present by handle (Codex #326 r3 P2 #2). The raw entry wins on a handle overlap
 * (it carries full data); a typed shim fills in any retained paper viewport the
 * raw list is missing. When the raw list is absent the result is exactly the
 * typed-shim list (the prior fallback). Dedup is by NORMALIZED handle.
 */
function mergeRawAndShimViewports(
  rawClipViewports: ReadonlyArray<DxfSupportedEntity> | undefined,
  paperViewports: ReadonlyArray<ViewportEntity>,
): DxfSupportedEntity[] {
  const raw = rawClipViewports ?? [];
  const seen = new Set<string>();
  for (const vp of raw) {
    const h = rawViewportHandle(vp);
    if (h) seen.add(h);
  }
  const merged: DxfSupportedEntity[] = [...raw];
  for (const shim of rawViewportShimsFromTyped(paperViewports)) {
    const h = rawViewportHandle(shim);
    // Skip any retained paper viewport already covered by a raw entry (raw wins);
    // a handle-less shim cannot collide, so always include it.
    if (h && seen.has(h)) continue;
    if (h) seen.add(h);
    merged.push(shim);
  }
  return merged;
}

/**
 * The literal "0" group-340 value is the documented "no spatial filter / no
 * clip" sentinel (Autodesk DXF reference AcDbViewport `340 default="0"`; ezdxf
 * `clipping_boundary_handle = DXFAttr(340, default="0")`). A viewport whose 340
 * is "0" is unclipped — it must NOT be treated as a boundary reference.
 */
const NO_CLIP_HANDLE = "0";

/**
 * Number of straight chords a bulged LWPOLYLINE clip segment is tessellated into.
 * A non-rectangular VPCLIP boundary feeds the renderer's stencil polygon, which
 * is a flat triangle list — an arc edge must be approximated by line segments.
 * 16 chords/arc keeps a smooth visual clip without exploding the vertex count
 * (the stencil is re-tessellated per-frame by earcut). Mirrors the chord density
 * other arc tessellators in the renderer use for closed boundaries.
 */
const BULGE_TESSELLATION_SEGMENTS = 16;

/**
 * Tessellate a CLOSED LWPOLYLINE boundary's edges into a flat list of polygon
 * vertices, expanding each bulged segment (non-zero `bulge`) into chords so an
 * arc-edged VPCLIP boundary becomes a faithful polygon rather than straight
 * chords between the bulge endpoints (Codex #326 P2). A straight segment
 * contributes only its start vertex; a bulged segment contributes its start
 * vertex plus the interior arc samples (the next segment's start vertex closes
 * it). The implicit closing edge (last→first, carrying the last vertex's bulge)
 * is expanded too so a circle authored as two bulged semicircles tessellates
 * correctly.
 */
function tessellatePolylineBoundary(
  poly: LwpolylineEntity,
): Array<{ x: number; y: number }> {
  const verts = poly.vertices.filter(
    (v) => Number.isFinite(v.x) && Number.isFinite(v.y),
  );
  const n = verts.length;
  const out: Array<{ x: number; y: number }> = [];
  // A closed polyline walks every edge incl. the implicit last→first; an open
  // one (defensive — caller already rejects open) walks vertex pairs only.
  const edgeCount = poly.closed ? n : n - 1;
  for (let i = 0; i < edgeCount; i++) {
    const v1 = verts[i]!;
    const v2 = verts[(i + 1) % n]!;
    // AutoCAD stores the bulge on the STARTING vertex of each segment.
    const arc = v1.bulge ? bulgeArc(v1, v2, v1.bulge) : null;
    out.push({ x: v1.x, y: v1.y });
    if (arc) {
      // Interior arc samples u ∈ (0, 1); u=0 is v1 (already pushed), u=1 is v2
      // (pushed as the NEXT segment's start), so emit only the strictly-interior
      // chord joints.
      for (let s = 1; s < BULGE_TESSELLATION_SEGMENTS; s++) {
        const p = bulgeArcPointAt(arc, s / BULGE_TESSELLATION_SEGMENTS);
        out.push({ x: p.x, y: p.y });
      }
    }
  }
  return out;
}

/**
 * Project a native group-340 clip-boundary ENTITY into the boundary's own
 * paper-space polygon (Codex #326 P2 round-2). VPCLIP permits a CLOSED LWPOLYLINE
 * / CIRCLE / ELLIPSE / closed SPLINE / REGION as the boundary; this resolves the
 * CLOSED LWPOLYLINE case faithfully, tessellating any bulged (arc) segments into
 * chords (Codex #326 P2). The returned points are in the boundary entity's
 * PAPER-space coordinate frame — the caller transforms them into the viewport's
 * MODEL-space frame (the frame the renderer's per-VP model camera draws the
 * stencil through). The curved boundary types (CIRCLE / ELLIPSE / closed SPLINE
 * / REGION) need their own tessellation and are a documented follow-on slice
 * (S-VIEWPORT-CLIP-340-CURVED) — they return null here so the caller surfaces
 * the viewport as an orphan-clip reference (visible gap, never silent).
 *
 * Returns null for: a missing boundary, a non-polyline / open polyline boundary,
 * or a degenerate (<3 vertex) polygon (a valid clip must enclose an area).
 *
 * Exported (VPCLIP-COMMAND) so the VPCLIP authoring producer
 * (`applyViewportClipSetPolygon`) can convert a user-selected clipping OBJECT
 * (VPCLIP "Select clipping object") into the same paper-space polygon the
 * import-time native-340 resolver derives — ONE truth for "closed boundary
 * entity → clip polygon", so an authored clip and an imported one tessellate
 * bulges identically (no simplification, ПРИОРИТЕТ #1).
 */
export function nativeBoundaryClipPolygon(
  boundary: DrawingEntity | undefined,
): Array<{ x: number; y: number }> | null {
  if (!boundary || boundary.type !== "LWPOLYLINE") return null;
  const poly = boundary as LwpolylineEntity;
  // A clip boundary must be a CLOSED area; an open polyline is not a clip region.
  if (!poly.closed) return null;
  const pts = tessellatePolylineBoundary(poly);
  if (pts.length < 3) return null;
  return pts;
}

/**
 * Transform a paper-space point into a viewport's MODEL-space (view) frame —
 * the inverse of the viewport's model→paper view transform. A native group-340
 * VPCLIP boundary lives in PAPER space (it is a paper-space entity drawn on the
 * sheet); the renderer draws the clip stencil through the per-VP MODEL camera
 * (`buildViewportModelCamera`, centred on `viewCenter` / sized by `viewHeight`),
 * so the boundary's paper coordinates MUST be mapped into model space or the
 * stencil polygon lands at the wrong location and clips the wrong geometry
 * (Codex #326 P1).
 *
 * The viewport maps a model window (`viewHeight` tall, centred at the WCS view
 * centre, rotated by `viewTwistAngle°`) onto its paper frame (`height` tall,
 * centred at `center`). Inverting: translate the paper point relative to the VP
 * paper centre, scale by `viewHeight / height` (model units per paper unit),
 * rotate by `+viewTwistAngle`, then offset by that centre. The centre is
 * {@link viewportWcsViewCenter} — `target + R(viewTwistAngle)·viewCenter`,
 * because groups 12/22 are in DCS, the DCS origin is the WCS view target
 * (17/27/37) and the DCS axes are turned by the twist; the SAME `+twist`
 * rotation is applied to the paper offset just below, which is what makes the
 * pair one rotation factored over a sum (`ezdxf` derivation in that helper) —
 * the
 * SAME anchor `buildViewportModelCamera` positions the per-VP camera with. It
 * has to be: this polygon is stencilled THROUGH that camera, so anchoring it on
 * the raw 12/22 pair while the camera sits at the summed centre would offset
 * the clip by the whole target vector and cut the wrong geometry. This mirrors
 * `scene3d/viewports/viewportTransform2d.paperToModel` with `paperOrigin = 0`
 * (the boundary entity's vertices and the viewport `center` share the SAME raw
 * DXF paper coordinate frame, so the page-setup origin cancels out of their
 * relative offset). Pure planar affine — `model.ts` carries no THREE dependency.
 */
function paperToViewportModel(
  paperPt: { x: number; y: number },
  vp: ViewportEntity,
): { x: number; y: number } {
  const dx = paperPt.x - vp.center.x;
  const dy = paperPt.y - vp.center.y;
  const scale = vp.height > 0 ? vp.viewHeight / vp.height : 1;
  const sx = dx * scale;
  const sy = dy * scale;
  const theta = ((vp.viewTwistAngle ?? 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const center = viewportWcsViewCenter(vp);
  return {
    x: center.x + (sx * cos - sy * sin),
    y: center.y + (sx * sin + sy * cos),
  };
}

/**
 * Re-project a native group-340 clip boundary from its retained PAPER-space
 * polygon (`clipBoundaryPaperSpace`) through the viewport's CURRENT paper→model
 * view transform, returning the fresh MODEL-space `clipPath` (Codex #326 r4 #1).
 *
 * A VPCLIP boundary is fixed on the paper sheet while the model view pans/zooms
 * underneath it (AutoCAD parity), so any command that changes the view fields
 * (`applyViewportSetView` pan/zoom, `applyViewportSetScale`) must call this to
 * keep the derived model-space `clipPath` aligned with the fixed paper boundary.
 *
 * Returns null when the viewport carries NO native paper-space boundary (a
 * SPATIAL_FILTER clip is fixed in MODEL/WCS space — its `clipPath` is already
 * correct under any view change and must NOT be recomputed) or the retained
 * polygon is degenerate (<2 points). The caller leaves the existing `clipPath`
 * untouched in that case.
 */
export function recomputeNativeClipPath(
  vp: ViewportEntity,
): Array<{ x: number; y: number }> | null {
  const paper = vp.clipBoundaryPaperSpace;
  if (!paper || paper.length < 2) return null;
  return paper.map((p) => paperToViewportModel(p, vp));
}

/**
 * Apply the two typed slice-3 projections (`clipPath` + `childEntityIds`) onto
 * the document's paper-space VIEWPORT entities IN PLACE. Returns the orphan clip
 * telemetry the caller folds into `importDiagnostics`.
 *
 * Both halves are skippable + independent so each import path runs only the half
 * for which it holds the inputs (see module header).
 *
 * @param document       the assembled DrawingDocument (mutated in place)
 * @param objectsSection the raw OBJECTS section (clip inputs); null/absent ⇒ no
 *                       clip resolution this call
 * @param blocks         block-name → definition map for membership block-bounds
 * @param options        per-path input/skip controls
 */
export function applyViewportClipAndMembership(
  document: DrawingDocument,
  objectsSection: DxfObjectsSection | null | undefined,
  blocks: Readonly<Record<string, BlockDefForBounds>>,
  options: ApplyViewportClipAndMembershipOptions = {},
): ApplyViewportClipAndMembershipResult {
  let orphanViewportClipReferences = 0;
  const paperViewports = collectPaperViewports(document);

  // ── (1) clipPath ──────────────────────────────────────────────────────────
  // Gate on the OBJECTS section + at least one paper viewport — NOT on a non-empty
  // `spatialFilters` list (Codex #326 r4 #2). A viewport whose ext-dict →
  // ACAD_FILTER → "SPATIAL" chain dangles to a MISSING SPATIAL_FILTER, in a
  // document carrying NO spatial filters at all, must still be walked so the
  // dangling chain is counted as an orphan (surfaced in importDiagnostics) rather
  // than the malformed import being reported CLEAN. `resolveViewportClip` is cheap
  // when no chain resolves (no dict ⇒ no orphan), so a clean filter-less document
  // costs only the per-viewport dict lookups.
  if (!options.skipClip && objectsSection && paperViewports.length > 0) {
    // MERGE the caller-supplied raw VIEWPORT list with a typed shim for EVERY
    // retained paper viewport NOT already covered by it (Codex #326 r3 P2 #2).
    //
    // The sync bridge passes the full pre-drain raw VIEWPORT list. The STREAMING
    // bridge, under `drainTopLevelEntities`, drains the top-level entities array
    // BEFORE this pass, so the raw list it can build contains ONLY whatever
    // VIEWPORTs survive in BLOCK definitions — a block-definition VIEWPORT, never
    // the retained TOP-LEVEL paper viewport (it was drained). The old
    // `options.clipViewports ?? shim` gate took the raw list WHOLESALE whenever it
    // was non-empty, so a streamed file with a block-def VIEWPORT + a top-level
    // clipped paper viewport resolved ONLY the block one and left the paper VP's
    // `clipPath` unset (it renders unclipped — the bug). Unioning the raw list
    // with shims for the uncovered paper viewports resolves BOTH: the raw entry
    // (full data) wins for any handle it carries, and the typed shim covers every
    // retained paper viewport the drained raw list is missing. `resolveViewportClip`
    // keys by handle (last write wins on a duplicate, same polygon either way), so
    // a benign handle overlap is safe. Dedup by NORMALIZED handle (DXF handles are
    // case-insensitive hex).
    const clipViewports = mergeRawAndShimViewports(
      options.clipViewports,
      paperViewports,
    );
    const clipResolution = resolveViewportClip(objectsSection, clipViewports);
    orphanViewportClipReferences =
      clipResolution.orphanViewportClipReferences;
    if (clipResolution.clipPathByViewportHandle.size > 0) {
      for (const vp of paperViewports) {
        const wcsPath = clipResolution.clipPathByViewportHandle.get(vp.id);
        if (!wcsPath || wcsPath.length < 2) continue;
        // The model `clipPath` is a 2D DrawingPoint[] (the clip plane is the
        // paper/WCS XY plane). Drop z (always 0 for a planar clip).
        vp.clipPath = wcsPath.map((p) => ({ x: p.x, y: p.y }));
        // Preserve the SPATIAL_FILTER boundary KIND (rectangular 2-pt vs
        // explicit polygon) captured by the resolver. `clipPath` alone cannot
        // convey it — a rectangular boundary is expanded to a 4-corner polygon,
        // so the inspector would mislabel a polygon clip whose group-90
        // non-rect bit is 0 as Rectangular without this source-preserved field
        // (@codex #496 P3).
        const kind = clipResolution.clipBoundaryKindByViewportHandle.get(vp.id);
        if (kind !== undefined) vp.clipBoundaryKind = kind;
      }
    }
  }

  // ── (1b) Native group-340 boundary-entity clip (Codex #326 P2 round-2) ──────
  // Per the Autodesk DXF reference, VIEWPORT group 340 is a "Hard-pointer to the
  // entity that serves as the viewport's clipping boundary (only present if the
  // viewport is non-rectangular)" — and ezdxf documents a non-rectangular
  // viewport as TWO objects: the viewport + a separate paper-space boundary
  // entity. A standard AutoCAD VPCLIP "Polygonal" / "specify points" clip stores
  // the boundary as that 340-referenced CLOSED LWPOLYLINE, NOT (necessarily) a
  // SPATIAL_FILTER. The SPATIAL_FILTER chain above only resolves the XCLIP-style
  // clip; a viewport whose ONLY clip artefact is the native 340 boundary would
  // otherwise get no `clipPath` and render rectangular (the bug). So for any
  // paper viewport still WITHOUT a `clipPath` that carries a REAL
  // `clippingBoundaryHandle` (≠ the "0" no-clip sentinel), resolve the boundary
  // entity from paper space and project its polygon. The boundary lives in PAPER
  // space, so its vertices are mapped through the viewport's paper→model view
  // transform (Codex #326 P1) — see `paperToViewportModel`.
  //
  // Scope: CLOSED LWPOLYLINE boundaries (the dominant VPCLIP case) resolve fully
  // and faithfully here, tessellating any bulged (arc) segments (Codex #326 P2).
  // The curved boundary types VPCLIP also permits (CIRCLE / ELLIPSE / closed
  // SPLINE / REGION) require their own tessellation into a stencil polygon — a
  // separate slice (S-VIEWPORT-CLIP-340-CURVED). Such a viewport is counted as an
  // orphan-clip reference so the gap is VISIBLE in import diagnostics rather than
  // silently rendering unclipped.
  //
  // Streaming: the worker SKIPS this half (`skipNativeBoundaryClip`) because the
  // referenced boundary entity is streamed out and not reliably retained in the
  // bridge shell; the main thread re-runs it with `paperSpaceEntities` from the
  // compact cache (Codex #326 P2 — worker:372).
  if (!options.skipNativeBoundaryClip && paperViewports.length > 0) {
    // Index every paper-space entity by NORMALIZED id so the 340 handle joins to
    // its boundary entity in O(1) regardless of hex case. DXF handles are
    // case-insensitive hex — a producer may write the VIEWPORT's 340 as `ab`
    // while the LWPOLYLINE handle (group 5) is `AB`; an exact-string lookup would
    // spuriously miss and leave the viewport rectangular (Codex #326 P2, mirrors
    // the OBJECTS/SPATIAL resolver's `normalizeHandle` keying).
    let paperEntitiesById: Map<string, DrawingEntity> | undefined;
    const buildPaperIndex = (): Map<string, DrawingEntity> => {
      const idx = new Map<string, DrawingEntity>();
      if (options.paperSpaceEntities) {
        // Streaming main-thread finalize: boundary entities come from the cache.
        for (const e of options.paperSpaceEntities) {
          idx.set(normalizeHandle(e.id), e);
        }
      } else {
        for (const space of document.spaces) {
          if (space.kind === "model") continue; // boundary lives in paper space
          for (const e of space.entities) idx.set(normalizeHandle(e.id), e);
        }
      }
      return idx;
    };
    for (const vp of paperViewports) {
      if (vp.clipPath !== undefined) continue; // already resolved (SPATIAL_FILTER)
      const handle = vp.clippingBoundaryHandle;
      if (handle === undefined || handle === NO_CLIP_HANDLE) continue;
      if (!paperEntitiesById) {
        paperEntitiesById = buildPaperIndex();
      }
      const boundary = paperEntitiesById.get(normalizeHandle(handle));
      const paperPolygon = nativeBoundaryClipPolygon(boundary);
      if (paperPolygon) {
        // The boundary polygon is in PAPER space; the renderer's per-VP model
        // camera draws the stencil in MODEL space, so map each vertex through the
        // viewport's paper→model view transform (Codex #326 P1). This keeps
        // `clipPath` uniformly in the MODEL/WCS frame — the same frame the
        // SPATIAL_FILTER path already produces (via the inverse-insert matrix) —
        // so the renderer needs no per-source branch.
        //
        // RETAIN the PAPER-space boundary (Codex #326 r4 #1): a native-340 VPCLIP
        // boundary is FIXED on the sheet, so when the user later pans/zooms this
        // viewport (applyViewportSetView / applyViewportSetScale) `clipPath` must
        // be RECOMPUTED from this paper-space source through the new view
        // transform. Without retaining the source, the derived model-space polygon
        // would stay frozen at the import-time view and the paper boundary would
        // drift off its fixed sheet position (the bug). `recomputeNativeClipPath`
        // re-projects it. (A SPATIAL_FILTER clip is fixed in MODEL space, carries
        // NO paper-space boundary, and is intentionally NOT recomputed.)
        vp.clipBoundaryPaperSpace = paperPolygon.map((p) => ({ x: p.x, y: p.y }));
        vp.clipPath = paperPolygon.map((p) => paperToViewportModel(p, vp));
      } else {
        // A real 340 boundary we cannot (yet) project to a polygon — surface it.
        orphanViewportClipReferences += 1;
      }
    }
  }

  // ── (2) childEntityIds (membership) ─────────────────────────────────────────
  // Independent of the clip boundary (a viewport always windows SOME model
  // content). Computed once over the model-space entities + every paper-space
  // VIEWPORT, then projected onto each viewport's `childEntityIds`.
  if (!options.skipMembership && paperViewports.length > 0) {
    // `modelSpaceEntities` is an `Iterable` (array OR lazy generator), walked
    // ONCE inside `buildViewportMembershipIndex`. No `.length` gate: an empty
    // iterable makes the membership build a cheap O(viewports) no-op (every VP
    // gets an empty member list, none assigned), and probing `.length` would
    // force a lazy generator to be unsupported / pre-materialised — the exact
    // O(entities) snapshot this path avoids (Codex #326 r3 P2 #4). Callers that
    // KNOW the model space is empty pass `skipMembership` instead.
    const modelSpaceEntities: Iterable<DrawingEntity> =
      options.modelSpaceEntities ??
      ((document.spaces.find((s) => s.kind === "model")?.entities ??
        []) as DrawingEntity[]);
    // Resolve every block definition's model-space AABB + base-point lookup so
    // a block INSERT tests its REAL footprint (def AABB transformed by the
    // insert, base point subtracted), not just its insertion point.
    // Unresolvable defs resolve to the conservative never-drop sentinel.
    const blockDefBounds = computeBlockDefinitionBounds(blocks);
    const membership = buildViewportMembershipIndex(
      modelSpaceEntities,
      paperViewports,
      blockDefBounds,
    );
    for (const vp of paperViewports) {
      const members = membership.get(vp.id);
      if (members && members.length > 0) vp.childEntityIds = members;
    }
  }

  return { orphanViewportClipReferences };
}
